<script lang="ts">
	import { afterNavigate } from '$app/navigation'
	import { prefs } from '#library/stores.ts'
	import { browser } from '$app/env'
	import { onMount } from 'svelte'
	import { page } from '$app/state'

	interface Props {
		ga_id?: string
		fb_id?: string
		debug?: boolean
	}

	let { ga_id, fb_id, debug = true }: Props = $props()

	function gtag(..._args: unknown[]) {
		window.dataLayer?.push(arguments)
	}

	function fbq(...args: unknown[]) {
		window.fbq?.(...args)
	}

	let ga_inited = false
	let fb_inited = false

	// initial mount
	onMount(() => {
		if (!browser || !$prefs.cookie || !page.data.production) return

		if (ga_id) {
			window.dataLayer = window.dataLayer || []
			gtag('js', new Date())
			gtag('config', ga_id)
			// only now can track() reach Google
			window.gtag = gtag
			ga_inited = true
		}

		if (fb_id && window.fbq && !fb_inited) {
			fbq('init', fb_id)
			fbq('track', 'PageView')
			fb_inited = true
		}
	})

	// SPA navigation tracking
	afterNavigate(({ to }) => {
		if (!browser || !$prefs.cookie || !page.data.production) return

		if (ga_id) {
			gtag('config', ga_id, {
				page_title: document.title,
				page_path: to?.url.pathname
			})
		}

		if (fb_id) fbq('track', 'PageView')
	})

	// keep GA consent updated
	$effect(() => {
		if (!browser || !ga_id || !page.data.production) return
		const consent = $prefs.cookie ? 'granted' : 'denied'
		gtag('consent', 'update', {
			ad_storage: consent,
			analytics_storage: consent,
			ad_user_data: consent,
			ad_personalization: consent
		})
	})

	// init GA if user flips cookies on after mount
	$effect(() => {
		if (!browser || !page.data.production) return
		if ($prefs.cookie && ga_id && !ga_inited) {
			window.dataLayer = window.dataLayer || []
			gtag('js', new Date())
			gtag('config', ga_id)
			// only now can track() reach Google
			window.gtag = gtag
			ga_inited = true
		}
	})

	// init FB if user flips cookies on after mount
	$effect(() => {
		if (!browser || !page.data.production) return
		if ($prefs.cookie && fb_id && window.fbq && !fb_inited) {
			fbq('init', fb_id)
			fbq('track', 'PageView')
			fb_inited = true
		}
	})

	$effect(() => {
		if (!browser || page.data.production || !debug) return

		console.groupCollapsed('🧐 Analytics')
		console.log('Not running in production, so not initialising analytics')
		console.table({
			prefs: $prefs.cookie,
			fb_id,
			fb_inited,
			ga_id,
			ga_inited
		})
		console.groupEnd()
	})
</script>

{#if !$prefs.cookie && fb_id && page.data.production}
	<img
		src="https://www.facebook.com/tr?id={fb_id}&ev=PageView&noscript=1"
		alt=""
		width="1"
		height="1"
		style="display:none"
		referrerpolicy="no-referrer"
		aria-hidden="true"
	/>
{/if}

<svelte:head>
	{#if $prefs.cookie && page.data.production}
		{#if ga_id}
			<!-- set default consent BEFORE loading gtag to avoid a race -->
			<script>
				window.dataLayer = window.dataLayer || []
				function gtag() {
					window.dataLayer.push(arguments)
				}
				gtag('consent', 'default', {
					ad_storage: 'denied',
					analytics_storage: 'denied',
					ad_user_data: 'denied',
					ad_personalization: 'denied'
				})
			</script>

			<script async src="https://www.googletagmanager.com/gtag/js?id={ga_id}"></script>
		{/if}

		{#if fb_id}
			<!-- bootstrap fbq so it exists before fbevents.js lands -->
			<script>
				!(function (f, b, e, v, n, t, s) {
					if (f.fbq) return
					n = f.fbq = function () {
						n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
					}
					if (!f._fbq) f._fbq = n
					n.push = n
					n.loaded = !0
					n.version = '2.0'
					n.queue = []
					t = b.createElement(e)
					t.async = !0
					t.src = v
					s = b.getElementsByTagName(e)[0]
					s.parentNode.insertBefore(t, s)
				})(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
			</script>
		{/if}
	{/if}
</svelte:head>
